import { useI18n } from 'vue-i18n';
import { useRuntimeConfig } from '#app';
import { computed } from 'vue';
import { localeMappings } from '~/i18n/config';

/**
 * 动态SEO元数据管理composable
 * 根据当前语言和页面上下文生成SEO meta标签
 */
export const useSeo = () => {
  const { t, locale } = useI18n();
  const config = useRuntimeConfig();
  const siteUrl = config.public.siteUrl || 'https://musicmint.ai';
  const ogLocale = () => localeMappings.shortToFull[locale.value as keyof typeof localeMappings.shortToFull] || 'en_US';

  /**
   * 获取基础SEO配置（所有页面通用）
   */
  const getBaseSeoConfig = () => {
    return {
      htmlAttrs: {
        lang: computed(() => locale.value),
      },
      link: [
        {
          key: 'canonical',
          rel: 'canonical',
          href: siteUrl + useRoute().path,
        },
      ],
    };
  };

  /**
   * 设置首页SEO元数据
   */
  const setHomePageSeo = () => {
    const title = () => t('meta.index.title');
    const description = () => t('meta.index.description');
    const keywords = () => t('meta.index.keywords');
    const ogTitle = () => t('meta.index.ogTitle');
    const ogDescription = () => t('meta.index.ogDescription');

    useSeoMeta({
      title,
      description,
      keywords,
      ogTitle,
      ogDescription,
      ogType: 'website',
      ogUrl: siteUrl,
      ogImage: `${siteUrl}/images/ogimage.png`,
      ogLocale,
      ogSiteName: 'MusicMint',
      twitterCard: 'summary_large_image',
      twitterTitle: ogTitle,
      twitterDescription: ogDescription,
      twitterImage: `${siteUrl}/images/ogimage.png`,
    });

    // 添加结构化数据（Schema.org）
    const jsonLd = computed(() => ({
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: 'MusicMint',
      url: siteUrl,
      description: description(),
      potentialAction: {
        '@type': 'SearchAction',
        target: `${siteUrl}/music/search?q={search_term_string}`,
        'query-input': 'required name=search_term_string',
      },
    }));

    useHead({
      ...getBaseSeoConfig(),
      script: [
        {
          type: 'application/ld+json',
          innerHTML: () => JSON.stringify(jsonLd.value),
        },
      ],
    });
  };

  /**
   * 设置创作页面SEO元数据
   */
  const setCreatePageSeo = () => {
    const title = () => t('meta.create.title');
    const description = () => t('meta.create.description');
    const keywords = () => t('meta.create.keywords');
    const ogTitle = () => t('meta.create.ogTitle');
    const ogDescription = () => t('meta.create.ogDescription');

    useSeoMeta({
      title,
      description,
      keywords,
      ogTitle,
      ogDescription,
      ogType: 'website',
      ogUrl: `${siteUrl}/create`,
      ogImage: `${siteUrl}/images/ogimage.png`,
      ogLocale,
      ogSiteName: 'MusicMint',
      twitterCard: 'summary_large_image',
      twitterTitle: ogTitle,
      twitterDescription: ogDescription,
      twitterImage: `${siteUrl}/images/ogimage.png`,
    });

    // 添加结构化数据（Schema.org） - WebApplication
    const jsonLd = computed(() => ({
      '@context': 'https://schema.org',
      '@type': 'WebApplication',
      name: 'MusicMint AI Music Creator',
      description: description(),
      url: `${siteUrl}/create`,
      applicationCategory: 'MultimediaApplication',
      operatingSystem: 'Any',
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'USD',
      },
      featureList: [
        'AI music generation',
        'Custom lyrics support',
        'Multiple genre support',
        'Tempo customization',
        'Style customization',
        'Instant music creation',
      ],
    }));

    useHead({
      ...getBaseSeoConfig(),
      script: [
        {
          type: 'application/ld+json',
          innerHTML: () => JSON.stringify(jsonLd.value),
        },
      ],
    });
  };

  /**
   * 设置歌词生成器页面SEO元数据
   */
  const setLyricsGeneratorSeo = () => {
    const title = () => t('meta.lyricsGenerator.title');
    const description = () => t('meta.lyricsGenerator.description');
    const keywords = () => t('meta.lyricsGenerator.keywords');
    const ogTitle = () => t('meta.lyricsGenerator.ogTitle');
    const ogDescription = () => t('meta.lyricsGenerator.ogDescription');

    useSeoMeta({
      title,
      description,
      keywords,
      ogTitle,
      ogDescription,
      ogType: 'website',
      ogUrl: `${siteUrl}/lyrics-generator`,
      ogImage: `${siteUrl}/images/ogimage.png`,
      ogLocale,
      ogSiteName: 'MusicMint',
      twitterCard: 'summary_large_image',
      twitterTitle: ogTitle,
      twitterDescription: ogDescription,
      twitterImage: `${siteUrl}/images/ogimage.png`,
    });

    useHead(getBaseSeoConfig());
  };

  /**
   * 设置Rap歌词生成器页面SEO元数据
   */
  const setRapLyricsGeneratorSeo = () => {
    const title = () => t('meta.rapLyricsGenerator.title');
    const description = () => t('meta.rapLyricsGenerator.description');
    const keywords = () => t('meta.rapLyricsGenerator.keywords');
    const ogTitle = () => t('meta.rapLyricsGenerator.ogTitle');
    const ogDescription = () => t('meta.rapLyricsGenerator.ogDescription');

    useSeoMeta({
      title,
      description,
      keywords,
      ogTitle,
      ogDescription,
      ogType: 'website',
      ogUrl: `${siteUrl}/rap-lyrics-generator`,
      ogImage: `${siteUrl}/images/ogimage.png`,
      ogLocale,
      ogSiteName: 'MusicMint',
      twitterCard: 'summary_large_image',
      twitterTitle: ogTitle,
      twitterDescription: ogDescription,
      twitterImage: `${siteUrl}/images/ogimage.png`,
    });

    useHead(getBaseSeoConfig());
  };

  /**
   * 设置Rap名称生成器页面SEO元数据
   */
  const setRapNameGeneratorSeo = () => {
    const title = () => t('meta.rapNameGenerator.title');
    const description = () => t('meta.rapNameGenerator.description');
    const keywords = () => t('meta.rapNameGenerator.keywords');
    const ogTitle = () => t('meta.rapNameGenerator.ogTitle');
    const ogDescription = () => t('meta.rapNameGenerator.ogDescription');

    useSeoMeta({
      title,
      description,
      keywords,
      ogTitle,
      ogDescription,
      ogType: 'website',
      ogUrl: `${siteUrl}/rap-name-generator`,
      ogImage: `${siteUrl}/images/ogimage.png`,
      ogLocale,
      ogSiteName: 'MusicMint',
      twitterCard: 'summary_large_image',
      twitterTitle: ogTitle,
      twitterDescription: ogDescription,
      twitterImage: `${siteUrl}/images/ogimage.png`,
    });

    useHead(getBaseSeoConfig());
  };

  /**
   * 设置人声分离器页面SEO元数据
   */
  const setVocalSeparationSeo = () => {
    const title = () => t('meta.vocalSeparation.title');
    const description = () => t('meta.vocalSeparation.description');
    const keywords = () => t('meta.vocalSeparation.keywords');
    const ogTitle = () => t('meta.vocalSeparation.ogTitle');
    const ogDescription = () => t('meta.vocalSeparation.ogDescription');

    useSeoMeta({
      title,
      description,
      keywords,
      ogTitle,
      ogDescription,
      ogType: 'website',
      ogUrl: `${siteUrl}/vocal-separation`,
      ogImage: `${siteUrl}/images/ogimage.png`,
      ogLocale,
      ogSiteName: 'MusicMint',
      twitterCard: 'summary_large_image',
      twitterTitle: ogTitle,
      twitterDescription: ogDescription,
      twitterImage: `${siteUrl}/images/ogimage.png`,
    });

    useHead(getBaseSeoConfig());
  };

  /**
   * 设置MV生成器页面SEO元数据
   */
  const setMvGeneratorSeo = () => {
    const title = () => t('meta.mvGenerator.title');
    const description = () => t('meta.mvGenerator.description');
    const keywords = () => t('meta.mvGenerator.keywords');
    const ogTitle = () => t('meta.mvGenerator.ogTitle');
    const ogDescription = () => t('meta.mvGenerator.ogDescription');

    useSeoMeta({
      title,
      description,
      keywords,
      ogTitle,
      ogDescription,
      ogType: 'website',
      ogUrl: `${siteUrl}/mv-generator`,
      ogImage: `${siteUrl}/images/ogimage.png`,
      ogLocale,
      ogSiteName: 'MusicMint',
      twitterCard: 'summary_large_image',
      twitterTitle: ogTitle,
      twitterDescription: ogDescription,
      twitterImage: `${siteUrl}/images/ogimage.png`,
    });

    useHead(getBaseSeoConfig());
  };

  /**
   * 设置音乐搜索页面SEO元数据
   */
  const setMusicSearchSeo = () => {
    const title = () => t('meta.musicSearch.title');
    const description = () => t('meta.musicSearch.description');
    const keywords = () => t('meta.musicSearch.keywords');
    const ogTitle = () => t('meta.musicSearch.ogTitle');
    const ogDescription = () => t('meta.musicSearch.ogDescription');

    useSeoMeta({
      title,
      description,
      keywords,
      ogTitle,
      ogDescription,
      ogType: 'website',
      ogUrl: `${siteUrl}/music/search`,
      ogImage: `${siteUrl}/images/ogimage.png`,
      ogLocale,
      ogSiteName: 'MusicMint',
      twitterCard: 'summary_large_image',
      twitterTitle: ogTitle,
      twitterDescription: ogDescription,
      twitterImage: `${siteUrl}/images/ogimage.png`,
    });

    useHead(getBaseSeoConfig());
  };

  /**
   * 设置音乐分类页面SEO元数据
   * @param params.title 分类显示名称（用于 meta title/description）
   * @param params.type 分类类型 ("category" 或 "rank")
   * @param params.path 页面路由路径（用于 canonical/ogUrl）
   * @param params.page 当前页码
   */
  const setMusicSectionSeo = (params: {
    title: string;
    type: 'category' | 'rank';
    path: string;
    page?: number;
  }) => {
    const { title: section, type, path, page } = params;

    const titleKey = 'meta.musicSection.title';
    const descKey = type === 'category'
      ? 'meta.musicSection.description_category'
      : type === 'rank'
      ? 'meta.musicSection.description_rank'
      : 'meta.musicSection.description';

    const keywordsKey = type === 'category'
      ? 'meta.musicSection.keywords_category'
      : type === 'rank'
      ? 'meta.musicSection.keywords_rank'
      : 'meta.musicSection.keywords';

    const ogTitleKey = type === 'category'
      ? 'meta.musicSection.ogTitle_category'
      : type === 'rank'
      ? 'meta.musicSection.ogTitle_rank'
      : 'meta.musicSection.ogTitle';

    const ogDescKey = type === 'category'
      ? 'meta.musicSection.ogDescription_category'
      : type === 'rank'
      ? 'meta.musicSection.ogDescription_rank'
      : 'meta.musicSection.ogDescription';

    const title = () => t(titleKey, { section });
    const description = () => t(descKey, { section });
    const keywords = () => t(keywordsKey, { section });
    const ogTitle = () => t(ogTitleKey, { section });
    const ogDescription = () => t(ogDescKey, { section });

    const canonicalUrl = page && page > 1
      ? `${siteUrl}${path}?page=${page}`
      : `${siteUrl}${path}`;

    useSeoMeta({
      title,
      description,
      keywords,
      ogTitle,
      ogDescription,
      ogType: 'website',
      ogUrl: canonicalUrl,
      ogImage: `${siteUrl}/images/ogimage.png`,
      ogLocale,
      ogSiteName: 'MusicMint',
      twitterCard: 'summary_large_image',
      twitterTitle: ogTitle,
      twitterDescription: ogDescription,
      twitterImage: `${siteUrl}/images/ogimage.png`,
    });

    useHead({
      htmlAttrs: { lang: computed(() => locale.value) },
      link: [{ key: 'canonical', rel: 'canonical', href: canonicalUrl }],
    });
  };

  /**
   * 设置音乐详情页面SEO元数据
   * @param song 歌曲数据对象
   */
  const setMusicSongSeo = (song: {
    id: string;
    title: string;
    description?: string;
    tags?: string;
    imageUrl?: string;
  }) => {
    const title = () => t('meta.musicSongId.title', { title: song.title });
    const description = () => t('meta.musicSongId.description', {
      title: song.title,
      tags: song.tags || '',
    });
    const keywords = () => t('meta.musicSongId.keywords', {
      title: song.title,
      tags: song.tags || '',
    });
    const ogTitle = () => t('meta.musicSongId.ogTitle', { title: song.title });
    const ogDescription = () => t('meta.musicSongId.ogDescription', {
      description: song.description || description(),
    });

    useSeoMeta({
      title,
      description,
      keywords,
      ogTitle,
      ogDescription,
      ogType: 'music.song',
      ogUrl: `${siteUrl}/music/song/${song.id}`,
      ogImage: song.imageUrl || `${siteUrl}/images/ogimage.png`,
      ogLocale,
      ogSiteName: 'MusicMint',
      twitterCard: 'summary_large_image',
      twitterTitle: ogTitle,
      twitterDescription: ogDescription,
      twitterImage: song.imageUrl || `${siteUrl}/images/ogimage.png`,
    });

    useHead(getBaseSeoConfig());
  };

  /**
   * 设置专辑封面生成器页面SEO元数据
   */
  const setAlbumCoverGeneratorSeo = () => {
    const title = () => t('meta.albumCoverGenerator.title');
    const description = () => t('meta.albumCoverGenerator.description');
    const keywords = () => t('meta.albumCoverGenerator.keywords');
    const ogTitle = () => t('meta.albumCoverGenerator.ogTitle');
    const ogDescription = () => t('meta.albumCoverGenerator.ogDescription');

    useSeoMeta({
      title,
      description,
      keywords,
      ogTitle,
      ogDescription,
      ogType: 'website',
      ogUrl: `${siteUrl}/album-cover-generator`,
      ogImage: `${siteUrl}/images/ogimage.png`,
      ogLocale,
      ogSiteName: 'MusicMint',
      twitterCard: 'summary_large_image',
      twitterTitle: ogTitle,
      twitterDescription: ogDescription,
      twitterImage: `${siteUrl}/images/ogimage.png`,
    });

    // Schema.org WebApplication 结构化数据
    const jsonLd = computed(() => ({
      '@context': 'https://schema.org',
      '@type': 'WebApplication',
      name: 'MusicMint AI Album Cover Generator',
      description: description(),
      url: `${siteUrl}/album-cover-generator`,
      applicationCategory: 'DesignApplication',
      operatingSystem: 'Any',
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'USD',
      },
      featureList: [
        'AI album cover generation',
        'Multiple artistic styles',
        'Reference image style transfer',
        'Custom artist name and album title',
        'Background image transformation',
        'Font style customization',
        'High-resolution 1024x1024 output',
      ],
    }));

    useHead({
      ...getBaseSeoConfig(),
      script: [
        {
          type: 'application/ld+json',
          innerHTML: () => JSON.stringify(jsonLd.value),
        },
      ],
    });
  };

  /**
   * 设置专辑封面分享页面SEO元数据（动态OG图片用于社交分享）
   */
  const setAlbumCoverShareSeo = (cover: { taskId: string; imageUrl?: string }) => {
    const title = () => t('meta.albumCoverShare.title');
    const description = () => t('meta.albumCoverShare.description');
    const ogTitle = () => t('meta.albumCoverShare.ogTitle');
    const ogDescription = () => t('meta.albumCoverShare.ogDescription');
    const image = cover.imageUrl || `${siteUrl}/images/ogimage.png`;

    useSeoMeta({
      title,
      description,
      ogTitle,
      ogDescription,
      ogType: 'website',
      ogUrl: `${siteUrl}/album-cover/${cover.taskId}`,
      ogImage: image,
      ogLocale,
      ogSiteName: 'MusicMint',
      twitterCard: 'summary_large_image',
      twitterTitle: ogTitle,
      twitterDescription: ogDescription,
      twitterImage: image,
    });

    useHead(getBaseSeoConfig());
  };

  return {
    setHomePageSeo,
    setCreatePageSeo,
    setLyricsGeneratorSeo,
    setRapLyricsGeneratorSeo,
    setRapNameGeneratorSeo,
    setVocalSeparationSeo,
    setMvGeneratorSeo,
    setMusicSearchSeo,
    setMusicSectionSeo,
    setMusicSongSeo,
    setAlbumCoverGeneratorSeo,
    setAlbumCoverShareSeo,
  };
};
